import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { SchedulePostMenu } from '@/components/post-form/SchedulePostMenu';
import { SimplePostForm } from '@/components/post-form/SimplePostForm';
import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';
import { content_edited } from '@/analytics';
import { minutesSince } from '@/analytics/contentAnalysis';

import { EditSimplePostModal_PostFragment_SimplePost } from './graphql-types/EditSimplePostModal_PostFragment';
import {
  EditSimplePostModal_UpdateSimplePost,
  EditSimplePostModal_UpdateSimplePostVariables,
} from './graphql-types/EditSimplePostModal_UpdateSimplePost';

import { EditPostModalFooter } from '../EditPostModalFooter';

const POST_FRAGMENT = gql`
  fragment EditSimplePostModal_PostFragment on PostV2 {
    _id
    datePublished
    dateCreated
    group {
      _id
    }
    organization {
      _id
    }
    sponsorData {
      isSponsoredPost
    }
    tags {
      _id
      name
    }
    embeddedImagesV2 {
      imageId
      imageProxy(input: { fit: "crop", ar: "4:3", h: 384 })
    }
    ... on SimplePost {
      simplePostTitle: title
      simplePostBody: body
      simplePostBodyContentState: bodyContentState {
        ...RawDraftContentStateFragment
      }
      scheduledPublishDate
      isAnnouncementScheduled
    }
    currentUserCanEditAnnouncedState
    versionFiles {
      _id
      file {
        _id
        name
        isVideo
        isImage
        url
      }
    }
  }
  ${RawDraftContentStateFragmentAST}
`;

const UPDATE_SIMPLE_POST = gql`
  mutation EditSimplePostModal_UpdateSimplePost(
    $input: UpdateSimplePostInput!
  ) {
    updateSimplePost(input: $input) {
      data {
        _id
        ...EditSimplePostModal_PostFragment
      }
    }
  }
  ${POST_FRAGMENT}
`;

type SimplePostFormValues = {
  title: string;
  group: string;
  images: ({ imageId: string } | { videoId: string })[];
  body: EditorState;
  isSponsoredPost: boolean;
  tags?: { _id: string; name: string }[];
  attachments: { versionFileId: string; fileName: string }[];
  scheduledPublishDate: Date | null;
  /** Only to be used when editing a scheduled draft post */
  isAnnouncement: boolean;
};

export const EditSimplePostModal: React.FC<{
  post: EditSimplePostModal_PostFragment_SimplePost;
  isOpen: boolean;
  onClose: () => void;
}> & { postFragment: DocumentNode } = ({ post, isOpen, onClose }) => {
  const isPublished = !!post.datePublished;
  const toast = useToast();
  const permissions = usePermissions();
  const canSchedulePosts = permissions.canDoAdminStuffAndAuthenticated;

  const [updateSimplePost] = useMutation<
    EditSimplePostModal_UpdateSimplePost,
    EditSimplePostModal_UpdateSimplePostVariables
  >(UPDATE_SIMPLE_POST, {
    refetchQueries: ['DraftsPage_GetMyDraftPosts', 'AccountMenu_Query'],
  });

  const methods = useForm<SimplePostFormValues>({
    defaultValues: {
      title: post.simplePostTitle ?? undefined,
      group: post.group?._id,
      images: post.embeddedImagesV2,
      body: post?.simplePostBody
        ? EditorState.createWithContent(convertFromRaw(post?.simplePostBody))
        : EditorState.createEmpty(),
      isSponsoredPost: post.sponsorData?.isSponsoredPost,
      tags: post.tags ?? [],
      attachments: post.versionFiles.map((files) => {
        return {
          versionFileId: files._id,
          fileName: files.file?.name || 'cant-load.file',
        };
      }),
      scheduledPublishDate: post.scheduledPublishDate
        ? new Date(post.scheduledPublishDate)
        : null,
      isAnnouncement: post.isAnnouncementScheduled,
    },
  });
  const { handleSubmit, formState, control } = methods;

  const onSubmit = async (params: SimplePostFormValues) => {
    try {
      await updateSimplePost({
        variables: {
          input: {
            simplePostId: post._id,
            ...(post.datePublished
              ? {}
              : { isAnnouncement: params.isAnnouncement }),
            data: {
              groupId: params.group,
              title: params.title,
              body: convertToRaw(params.body.getCurrentContent()),
              versionFileIds: params.attachments.map(
                (file) => file.versionFileId
              ),
              tagIds: params.tags?.map((tag) => tag._id),
              embeddedImageIds: params.images
                .filter((id): id is { imageId: string } => 'imageId' in id)
                .map((id) => id.imageId),
              scheduledPublishDate: post.datePublished
                ? undefined
                : params.scheduledPublishDate?.toISOString() ?? null,
            },
          },
        },
      });

      // Track content edit
      content_edited({
        entity: {
          id: post._id,
          type: 'post',
        },
        content: {
          contentType: 'post',
        },
        timing: {
          timeSinceCreation: (post as any).dateCreated ? minutesSince((post as any).dateCreated) : 0,
        },
      });

      onClose();

      toast({
        description: !!params.scheduledPublishDate
          ? `Post scheduled for ${dayjs(params.scheduledPublishDate).format(
            'MMM D, h:mm A'
          )}`
          : 'Post updated',
        status: 'success',
      });
    } catch (e) {
      toast({
        status: 'error',
        description: 'Failed to update post.',
      });
      logError(e);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        methods.reset();
        onClose();
      }}
      size="xl"
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isPublished ? 'Edit Post' : 'Edit Draft Post'}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px="6" py="4">
          <FormProvider {...methods}>
            <SimplePostForm
              organizationId={post.organization?._id}
              initialGroupId={post.group?._id}
              originalImages={post.embeddedImagesV2.filter(
                (
                  image
                ): image is {
                  imageId: string;
                  imageProxy: string;
                  __typename: 'PostEmbeddedImage';
                } => {
                  return !!image.imageProxy;
                }
              )}
              isEditing
              isPublished={!!post.datePublished}
            />
          </FormProvider>
        </ModalBody>
        <EditPostModalFooter>
          <HStack>
            {!isPublished && canSchedulePosts && (
              <Controller
                name="scheduledPublishDate"
                control={control}
                render={({ field: { onBlur, onChange, ref, value, name } }) => (
                  <SchedulePostMenu
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    isDisabled={formState.isSubmitted && !formState.isValid}
                    validate={() => {
                      if (!value) {
                        methods.setError(name, {
                          message: 'A date and time must be specified',
                        });
                        return false;
                      } else {
                        methods.clearErrors(name);
                        return true;
                      }
                    }}
                    validationMessage={
                      formState.errors.scheduledPublishDate?.message
                    }
                    shouldRestrictUnsettingValue
                  />
                )}
              />
            )}
            <Button
              isDisabled={formState.isSubmitted && !formState.isValid}
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </HStack>
        </EditPostModalFooter>
      </ModalContent>
    </Modal>
  );
};

EditSimplePostModal.postFragment = POST_FRAGMENT;
